import { trigger, transition, style, animate } from '@angular/animations';
import { AfterViewInit, Component, ElementRef, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { ModalComponent } from 'src/app/components/modal/modal.component';
import { NotificationService } from 'src/app/services/notification.service';
import { TabControlService } from 'src/app/services/tab-control.service';
import { SessionData, decodeSessionData, hideEmailAddress } from 'src/app/util/Helper';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-main',
  templateUrl: './trust.component.html',
  styleUrls: ['./trust.component.scss'],
  animations: [
    trigger('slideIn', [
      transition(':enter', [
        style({ transform: 'translateX(-100%)', opacity: 0 }),
        animate('300ms', style({ transform: 'translateX(0)', opacity: 1 })),
      ]),
    ]),
  ],
})
export class TrustComponent implements AfterViewInit, OnInit {
  @ViewChild('modalContent') modalContent!: TemplateRef<any>;
  @ViewChild('scroller', { static: false }) scroller?: ElementRef;

  // Url Params
  session: string = ""; // used to resume session

  
  tabsAnimationState: boolean[] = [false, false, false, false, false, false, false];

  MileleLogoPath: string  = `${environment.hRef}assets/Milele_logo_white.png`;
  logoPath: string  = `${environment.hRef}assets/logo_w.png`;
  forumPath: string  = `${environment.hRef}assets/forum-g.png`;

  loginText: string = 'Login';
  existingCustomerText: string = 'Already an existing Milele Customer?';

  chatUsText: string = 'Chat with us. How may we help?';

  personalData: string = 'Personal Data';
  addSettlor: string = 'Settlor';
  addBeneficiary: string = 'Beneficiary';
  addGuardian: string = 'Guardian';
  trustInfo: string = 'Trust Info';
  preview: string = 'Preview';
  payment: string = 'Payment';
  powerdByText: string = 'Powered by ICEA LION Group. All rights reserved.';

  hasInfoPopupRun: boolean = false;

  otpForm = this.formBuilder.group({
    otp: ['']
  });

  ngAfterViewInit() {
    // Trigger the animation for each tab after a slight delay
    this.animateTabs();
  }

  constructor(
    private readonly router: Router,
    private readonly formBuilder: FormBuilder,
    private readonly route: ActivatedRoute,
    private readonly dialog: MatDialog,
    private readonly tabControlService: TabControlService,
    private readonly notificationService: NotificationService
  ) {
  }

  ngOnInit(): void {
    // UNCOMMENT TO FEED TEST DATA
    // this.tabControlService.updateDataFromStorage(testFundData);


    this.route.queryParams.subscribe(async (params) => {
      if (params["session"] == undefined) {
        const data = await this.tabControlService.getData(this.tabControlService.storageKey);

        if (data) {
          if (!environment.production) console.log('Trust Data\n', data);
          await this.tabControlService.updateDataFromStorage();
        }
      } else {
        this.session = params["session"];
        this.tabControlService.session = this.session;
        await this.initResume(this.session);
      }

      if (this.activeTab == 1 && !this.hasInfoPopupRun){ this.openInfoModal(); }
    });
    
  }

  // Type guard function to check if an object matches the SessionData type
  isSessionData(obj: any): obj is SessionData {
    return (
      typeof obj === 'object' &&
      obj !== null &&
      typeof obj.SessionId === 'string'
    );
  }

  async initResume(session: string): Promise<void> {
    const sessionData = decodeSessionData(session);
    if (this.isSessionData(sessionData) && sessionData.SessionId && sessionData.SessionId !== '') {
      await this.tabControlService.checkProgress(session);
      if (this.onlineStorageObject) {
        this.otpEmail = hideEmailAddress(this.onlineStorageObject.basicData.Email);
      }

    } else {
      const data = await this.tabControlService.getData(this.tabControlService.storageKey);
      if (data) {
        this.tabControlService.updateDataFromStorage();
      }
    }
  }

  animateTabs() {
    for (let i = 0; i < this.tabsAnimationState.length; i++) {
      setTimeout(() => {
        this.tabsAnimationState[i] = true;
      }, 400 * i); // Adjust the delay as needed
    }
  }

  otpReady: boolean = false;
  otpValue: number = 0;
  otpLoading: boolean = false;
  otpEmail:string = 'that was used to set up the trust';

  get activeTab(): number {
    return this.tabControlService.activeTab;
  }
  get regionType(): number {
    return this.tabControlService.regionType;
  }
  get page1done(): boolean {
    return this.tabControlService.page1Done;
  };
  get page2done(): boolean {
    return this.tabControlService.page2Done;
  };
  get page3done(): boolean {
    return this.tabControlService.page3Done;
  };
  get page4done(): boolean {
    return this.tabControlService.page4Done;
  };
  get page5done(): boolean {
    return this.tabControlService.page5Done;
  };
  get page6done(): boolean {
    return this.tabControlService.page6Done;
  };

  get loading(): boolean {
    return this.notificationService.loading;
  };

  get onlineStorageObject(): any {
    return this.tabControlService.onlineStorageObject;
  }

  get showResumePopup(): boolean {
    return this.tabControlService.showResumePopup;
  };
  

  changeTab(tabNumber: number) {
    this.tabControlService.changeTab(tabNumber);
  }

  navHome(): void {
    const nextPage = ``;
    if (!this.session || this.session == '') {this.router.navigate([nextPage]);}
    else {this.router.navigate([nextPage], {queryParams: {session: this.session}});}
  }

  scroll(childDivHeight: number) {
    if (this.scroller) this.scroller.nativeElement.scrollTop = childDivHeight;
  }

  onOtpChange(otp: string) {
    if (otp.length === 6) {
      this.otpReady = true;
      this.otpValue = Number.parseInt(otp);
    } else {
      this.otpReady = false;
    }
  }

  async verifyOTP(): Promise<void> {
    if (!this.otpLoading) {
      try {
        this.otpForm.disable();
        if (this.otpValue === this.onlineStorageObject.OTP) {
          // update data
          await this.tabControlService.updateDataFromStorage(this.onlineStorageObject);
          this.notificationService.viewToast('info', 'Session resumed');
        } else {
          this.notificationService.viewToast('error', 'Invalid OTP provided');
        }
      } catch (error) {
        console.error('Resume OTP : ', error);
        this.notificationService.viewToast('error', 'An error occurred');
      }
      this.closeOTPPopup();
    }
  }

  closeOTPPopup(): void {
    if (!this.otpLoading) {
      this.tabControlService.showResumePopup = false;
    }
  }

  // Session info Popup
  openInfoModal = (): void => {
    const dialogRef = this.dialog.open(ModalComponent, {
      maxWidth: '500px',  // Maximum width of the modal
      height: 'fit-content',
      maxHeight: '1100px',
      data: {
        contentTemplate: this.modalContent,
        modalTitle: 'Welcome To Milele Trust Fund',
        buttonText: 'Okay'
      },
      disableClose: true // Disable closing when clicking outside the modal
    });

    this.notificationService.addModal(dialogRef);

    dialogRef.afterClosed().subscribe(() => {
      this.notificationService.removeModal(dialogRef);
      this.hasInfoPopupRun = true;
    });
  }
}
